import { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchMtrList } from '#/api/imsMgmt/settings/mtr/fetchList';
import { createBom } from '#/api/momMgmt/settings/bom/create';
import { fetchBomDetail } from '#/api/momMgmt/settings/bom/fetchDetail';
import { updateBom } from '#/api/momMgmt/settings/bom/update';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import {
  getMtrApiErrorMessage,
} from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import PageShell from '#/modules/infra/layout/PageShell';
import BomExtraMaterialsEditor from '#/modules/momMgmt/settings/bom/components/BomExtraMaterialsEditor';
import BomGroupsPositionsEditor from '#/modules/momMgmt/settings/bom/components/BomGroupsPositionsEditor';
import BomMtrLinkSection from '#/modules/momMgmt/settings/bom/components/BomMtrLinkSection';
import {
  getBomApiErrorMessage,
  isBomVersionConflict,
} from '#/modules/momMgmt/settings/bom/helper/apiError';
import {
  mapDetailExtraMtrsToForm,
  mapDetailGroupsToForm,
  withBomCopySuffix,
} from '#/modules/momMgmt/settings/bom/helper/bomGroupsForm';
import { collectBomPositionDuplicates } from '#/modules/momMgmt/settings/bom/helper/bomPositionDuplicate';
import {
  canActivateBom,
  canDeactivateBom,
} from '#/modules/momMgmt/settings/bom/helper/bomStatus';
import { syncBomLinkedMaterial } from '#/modules/momMgmt/settings/bom/helper/syncBomLinkedMaterial';
import useBomStatusMutation, {
  BOM_ACTIVATE_CONFIRM,
  BOM_DEACTIVATE_CONFIRM,
} from '#/modules/momMgmt/settings/bom/hooks/useBomStatusMutation';
import {
  BOM_FIELD_LABELS,
  bomFormSchema,
  resolveBomFieldLabel,
} from '#/modules/momMgmt/settings/bom/schemas/bomSchema';
import {
  bomDetailQueryKey,
  bomListQueryKeyPrefix,
  mtrListQueryKey,
  mtrListQueryKeyPrefix,
} from '#/utils/queryKeys';

function formatBomPositionDuplicateMessages(duplicates) {
  return duplicates.map((item) => {
    const locations = item.locations
      .map((location) => (
        `${location.groupName || '（未命名群組）'} - ${location.mtrNo || location.mtrName || '—'}`
      ))
      .join('、');
    return `位置名稱「${item.positionName}」重複使用：${locations}`;
  });
}

const DEFAULT_VALUES = {
  code: '',
  name: '',
  version: '',
  remark: '',
  groups: [],
  extraMaterials: [],
  linkedMtrId: '',
  linkedMtrNo: '',
  linkedMtrName: '',
};

function getBomSaveErrorMessage(error) {
  const code = error?.response?.data?.code || error?.response?.data?.error?.code;
  if (code === 'BOM_INACTIVE' || code === 'MTR_NOT_FOUND') {
    return getMtrApiErrorMessage(error, getBomApiErrorMessage(error, '儲存失敗'));
  }
  return getBomApiErrorMessage(error, '儲存失敗');
}

const CreateEditBomPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const isCopy = Boolean(id) && location.pathname.endsWith('/copy');
  const isEditMode = Boolean(id) && !isCopy;
  const updatedAtRef = useRef(null);
  const initialLinkedMtrIdRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
    setCustomMessages,
    clearCustomMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: bomDetailQueryKey(id),
    queryFn: () => fetchBomDetail(id),
    enabled: Boolean(id),
  });

  const linkedMtrListQuery = useMemo(() => ({
    page: 1,
    pageSize: 1,
    keyword: '',
    sort: [],
    filters: { bomId: id },
  }), [id]);

  const linkedMtrQuery = useQuery({
    queryKey: mtrListQueryKey(linkedMtrListQuery),
    queryFn: () => fetchMtrList(linkedMtrListQuery),
    enabled: isEditMode && Boolean(id),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(bomFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { isPending, save } = useCreateEditSave({
    id: isEditMode ? id : null,
    mutationFn: async (values) => {
      // 表單 values（groups[].materials／extraMaterials）直接交 API；
      // 勿在此先 buildBomWritePayload，否則 create／update 再 serialize 會讀不到 materials → mtrs:[]。
      const writeValues = {
        code: values.code,
        name: values.name,
        version: values.version,
        remark: values.remark || null,
        groups: values.groups ?? [],
        extraMaterials: values.extraMaterials ?? [],
      };

      let bomResult;
      let bomId;
      if (isEditMode) {
        bomResult = await updateBom(id, {
          ...writeValues,
          updatedAt: updatedAtRef.current,
        });
        bomId = id;
        const nextUpdatedAt = bomResult?.data?.updatedAt
          ?? bomResult?.data?.record?.updatedAt;
        if (nextUpdatedAt) {
          updatedAtRef.current = nextUpdatedAt;
        }
      } else {
        bomResult = await createBom(writeValues);
        bomId = bomResult?.data?.id;
      }

      if (!bomId) {
        throw new Error('BOM 儲存成功但缺少 id');
      }

      await syncBomLinkedMaterial({
        bomId,
        previousMtrId: isEditMode ? initialLinkedMtrIdRef.current : null,
        nextMtrId: values.linkedMtrId || null,
      });

      initialLinkedMtrIdRef.current = values.linkedMtrId || null;
      await queryClient.invalidateQueries({ queryKey: mtrListQueryKeyPrefix() });
      return bomResult;
    },
    listQueryKeyPrefix: bomListQueryKeyPrefix,
    detailQueryKey: bomDetailQueryKey,
    getEditPath: ROUTES.bomEdit,
    getErrorMessage: getBomSaveErrorMessage,
    isVersionConflict: isBomVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const statusMutation = useBomStatusMutation({
    id,
    getUpdatedAt: () => updatedAtRef.current,
    onVersionConflict: () => {
      if (id) {
        detailQuery.refetch();
      }
    },
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    if (isEditMode && linkedMtrQuery.isLoading) {
      return;
    }

    if (isEditMode) {
      updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    } else {
      updatedAtRef.current = null;
    }

    const linkedRow = isEditMode
      ? (linkedMtrQuery.data?.data?.[0] ?? null)
      : null;
    const linkedMtrId = linkedRow?.id ?? '';
    if (isEditMode) {
      initialLinkedMtrIdRef.current = linkedMtrId || null;
    }
    if (isCopy) {
      initialLinkedMtrIdRef.current = null;
    }

    resetValidationSummary();
    reset({
      code: isCopy ? withBomCopySuffix(detail.code) : (detail.code ?? ''),
      name: detail.name ?? '',
      version: isCopy ? withBomCopySuffix(detail.version) : (detail.version ?? ''),
      remark: detail.remark ?? '',
      groups: mapDetailGroupsToForm(detail.groups),
      extraMaterials: mapDetailExtraMtrsToForm(detail),
      linkedMtrId: isCopy ? '' : linkedMtrId,
      linkedMtrNo: isCopy ? '' : (linkedRow?.mtrNo ?? ''),
      linkedMtrName: isCopy ? '' : (linkedRow?.name ?? ''),
    });
  }, [
    detailQuery.data,
    linkedMtrQuery.data,
    linkedMtrQuery.isLoading,
    isCopy,
    isEditMode,
    reset,
    resetValidationSummary,
  ]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    const duplicates = collectBomPositionDuplicates(values.groups);
    if (duplicates.length > 0) {
      setCustomMessages(formatBomPositionDuplicateMessages(duplicates));
      return;
    }
    clearCustomMessages();
    save(values);
  }

  const pageTitle = isCopy ? '複製 BOM' : (isEditMode ? '編輯 BOM' : '新增 BOM');
  const needsSourceDetail = isEditMode || isCopy;
  const isPageLoading = needsSourceDetail && (
    detailQuery.isLoading || (isEditMode && linkedMtrQuery.isLoading)
  );
  const isDetailNotFound = needsSourceDetail && detailQuery.isError;
  const detail = detailQuery.data?.data;
  const bomStatus = detail?.status;
  const canActivate = isEditMode && canActivateBom(bomStatus);
  const canDeactivate = isEditMode && canDeactivateBom(bomStatus);
  const isBusy = isPending || statusMutation.isPending;

  async function handleActivate() {
    const ok = await confirm(BOM_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(BOM_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.bom}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getBomApiErrorMessage(detailQuery.error, '找不到 BOM 資料')}
            </p>
          ) : (
            <>
              {ConfirmDialogHost}
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={BOM_FIELD_LABELS}
                fieldLabelResolver={resolveBomFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body space-y-4">
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-5',
                      '@4xl/sheet:grid-cols-[minmax(0,3fr)_1px_minmax(0,7fr)]',
                    )}
                  >
                    <div className="form-block min-w-0 space-y-4">
                      <div className="form-section__header">
                        <h2 className="form-block__title mb-0! mr-2">基本資訊</h2>
                        {canDeactivate ? (
                          <Button
                            type="button"
                            className="ml-auto"
                            size="sm"
                            variant="destructive"
                            disabled={isBusy}
                            onClick={handleDeactivate}
                          >
                            停用 BOM
                          </Button>
                        ) : null}
                        {canActivate ? (
                          <Button
                            type="button"
                            className="ml-auto"
                            size="sm"
                            disabled={isBusy}
                            onClick={handleActivate}
                          >
                            啟用 BOM
                          </Button>
                        ) : null}
                      </div>
                      <div className="mt-3 space-y-4">
                        <FloatingInput
                          id="code"
                          type="text"
                          label="BOM代號"
                          register={register}
                          errors={errors}
                        />
                        <FloatingInput
                          id="name"
                          type="text"
                          label="BOM名稱"
                          register={register}
                          errors={errors}
                        />
                        <FloatingInput
                          id="version"
                          type="text"
                          label="版本號"
                          register={register}
                          errors={errors}
                        />
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                        />
                      </div>

                      <BomMtrLinkSection
                        control={control}
                        setValue={setValue}
                        disabled={isBusy}
                      />
                    </div>

                    <div
                      className={cn(
                        'border-b-2 border-dashed border-gray-400',
                        '@4xl/sheet:border-b-0 @4xl/sheet:border-l-2',
                      )}
                      aria-hidden
                    />

                    <BomExtraMaterialsEditor
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  <BomGroupsPositionsEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.bom}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isBusy}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditBomPage;
